import { captureMessage, addBreadcrumb } from '@sentry/browser';

interface ChunkLoadDiagnostic {
    originalError: string | undefined,
    chunkUrl: string | undefined,
    pageUrl: string,
    timestamp: number,
    online: boolean,
    effectiveType?: string,
    downlink?: number,
    rtt?: number,
    retryStatus?: number,
    retryOk?: boolean,
    retryDurationMs?: number,
    retryError?: string,
    retryContentType?: string,
    retryContentLength?: string,
    retryHeaders?: Record<string, string | null>,
    timeSincePageLoadMs?: number,
    visibilityState?: string,
    hidden?: boolean,
    navigationType?: string,
    hasServiceWorker?: boolean
}

function getNetworkInfo(): Pick<ChunkLoadDiagnostic, 'effectiveType' | 'downlink' | 'rtt'> {
    const conn = (navigator as any).connection;
    if (!conn) {
        return {};
    }
    return {
        effectiveType: conn.effectiveType,
        downlink: conn.downlink,
        rtt: conn.rtt
    };
}

function extractChunkUrl(message?: string): string | undefined {
    const match = message?.match(/https?:\/\/[^\s'"]+/);
    return match?.[0];
}

interface VitePreloadErrorEvent extends Event {
    payload: Error
}

window.addEventListener('vite:preloadError', ((event: VitePreloadErrorEvent) => {
    const { payload } = event;
    const chunkUrl = extractChunkUrl(payload?.message);

    // Prevent Vite from throwing, which would silently kill the app bootstrap.
    // The missing module will cause a downstream error that React error boundaries can catch.
    event.preventDefault();

    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;

    const diagnostic: ChunkLoadDiagnostic = {
        originalError: payload?.message,
        chunkUrl,
        pageUrl: window.location.href,
        timestamp: Date.now(),
        online: navigator.onLine,
        timeSincePageLoadMs: Math.round(performance.now()),
        visibilityState: document.visibilityState,
        hidden: document.hidden,
        navigationType: navEntry?.type,
        hasServiceWorker: navigator.serviceWorker?.controller != null,
        ...getNetworkInfo()
    };

    // Add breadcrumb so the diagnostic appears on the subsequent error event
    addBreadcrumb({
        category: 'chunk-load',
        message: `Chunk load failed: ${chunkUrl ?? 'unknown'}`,
        level: 'warning',
        data: diagnostic
    });

    // Probe the failed URL to determine why it failed (404, network error, etc.)
    if (chunkUrl) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000);
        const fetchStart = performance.now();

        fetch(chunkUrl, { cache: 'no-store', signal: controller.signal })
            .then(response => {
                clearTimeout(timeout);
                diagnostic.retryStatus = response.status;
                diagnostic.retryOk = response.ok;
                diagnostic.retryDurationMs = Math.round(performance.now() - fetchStart);
                diagnostic.retryContentType = response.headers.get('content-type') ?? undefined;
                diagnostic.retryContentLength = response.headers.get('content-length') ?? undefined;
                diagnostic.retryHeaders = {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'cache-control': response.headers.get('cache-control'),
                    server: response.headers.get('server'),
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    'x-powered-by': response.headers.get('x-powered-by'),
                    age: response.headers.get('age')
                };
            })
            .catch((retryError: unknown) => {
                clearTimeout(timeout);
                diagnostic.retryStatus = 0;
                diagnostic.retryDurationMs = Math.round(performance.now() - fetchStart);
                diagnostic.retryError = retryError instanceof Error
                    ? retryError.message
                    : String(retryError);
            })
            .finally(() => {
                captureMessage('Chunk load failure', {
                    level: 'warning',
                    extra: { diagnostic }
                });
            });
    } else {
        captureMessage('Chunk load failure', {
            level: 'warning',
            extra: { diagnostic }
        });
    }
}) as EventListener);
